import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import {
  Breadcrumbs,
  Button,
  Grid,
  Paper,
  TableContainer,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router';
import { Link } from 'react-router-dom';
import * as api from '../../api/app/assetApi';
import { AlertDialog } from '../../components/core/AlertDialog';
import { ApiError } from '../../components/core/ApiError/ApiError';
import DataGridTable from '../../components/core/DataGridTable/DataGridTable';
import { Loader } from '../../components/core/Loader/Loader';
import { useTranslation } from '../../contexts/translation';
import { useDeleteDialog } from '../../hooks/useDeleteDialog';
import { Asset, AssetType, assetTypes } from '../../types/app/asset.types';
import { paths } from '../../utils/core/routes';

interface Props {
  pathList: Asset[];
  isReadOnly: boolean;
}

export function AssetChildList({ pathList, isReadOnly }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const theme = useTheme();
  const {
    isOpen: isDeleteDialogOpen,
    idToDelete,
    setDeleteAttempt,
    cancelDeleteAttempt,
    confirmDeleteAttempt,
  } = useDeleteDialog();
  const { id } = useParams();
  const parentId: number = Number(id);

  const { data, isLoading, isError } = useQuery<Asset[]>(['assets', parentId], () =>
    api.getAssetList(parentId),
  );
  const { mutate: mutateDeleteAsset } = useMutation(api.deleteAsset);

  const columns = [
    { headerName: 'Id', field: 'id', width: 120 },
    {
      headerName: 'Asset type',
      field: 'assetType',
      width: 200,
      renderCell: (t: any) =>
        assetTypes.map((type: AssetType) => {
          if (type.code === t.row.assetType) return type.name;
        }),
    },
    { headerName: 'Name', field: 'name', width: 200 },
    {
      headerName: 'Influx machine name',
      field: 'influxMachineName',
      width: 200,
    },
    {
      field: '',
      sortable: false,
      disableColumnMenu: true,
      width: 150,
      renderCell: (params: any) => {
        return (
          <>
            {isReadOnly && (
              <Tooltip title={t('general.action-buttons.detail')}>
                <Button onClick={() => navigate(`${paths.ASSETS_DETAIL}/${params.id}`)}>
                  <RemoveRedEyeOutlinedIcon />
                </Button>
              </Tooltip>
            )}
            {!isReadOnly && (
              <>
                <Tooltip title={t('general.action-buttons.edit')}>
                  <Button onClick={() => navigate(`${paths.ASSETS_DETAIL}/${params.id}`)}>
                    {!isReadOnly && <EditIcon />}
                    {isReadOnly && <RemoveRedEyeOutlinedIcon />}
                  </Button>
                </Tooltip>
                <Tooltip title={t('general.action-buttons.delete')}>
                  <Button onClick={() => setDeleteAttempt(params.id)}>
                    <DeleteIcon />
                  </Button>
                </Tooltip>
              </>
            )}
          </>
        );
      },
    },
  ];

  const handleOnCreate = () =>
    parentId
      ? navigate(`${paths.ASSETS_CREATE}/${parentId}`, { state: pathList })
      : navigate(paths.ASSETS_CREATE);

  const handleConfirm = () => {
    if (!idToDelete) return;

    mutateDeleteAsset(idToDelete, {
      onSuccess: () => {
        enqueueSnackbar(t('page.asset.item-deleted'), { variant: 'success' });
        queryClient.invalidateQueries(['assets']);
        confirmDeleteAttempt();
      },
      onError: (result: any) => {
        enqueueSnackbar(`${result}`, { variant: 'error' });
      },
    });
  };

  if (isError) return <ApiError />;
  if (isLoading || !data) return <Loader />;

  return (
    <>
      <Grid container>
        <Grid item xs={12} mb={3}>
          <Breadcrumbs aria-label="breadcrumb">
            {pathList.map((item, index: number) =>
              pathList.length - 1 === index ? (
                <Typography key={index} sx={{ color: theme.palette.primary.dark }}>
                  {item.name}
                </Typography>
              ) : (
                <Link
                  key={item.id}
                  to={`${paths.ASSETS_DETAIL}/${item.id}`}
                  style={{ color: theme.palette.primary.main, fontWeight: 600 }}
                >
                  {item.name}
                </Link>
              ),
            )}
          </Breadcrumbs>
        </Grid>
      </Grid>
      <Grid item>
        <TableContainer component={Paper}>
          <div style={{ height: 405, width: '100%' }}>
            <DataGridTable height={405} data={data} columns={columns} autoPageSize={true} />
          </div>
          {!isReadOnly && (
            <Tooltip title={t('general.action-buttons.new')}>
              <Button onClick={handleOnCreate}>
                <AddIcon />
              </Button>
            </Tooltip>
          )}
        </TableContainer>
      </Grid>
      <AlertDialog
        open={isDeleteDialogOpen}
        title={t('general.action-buttons.delete')}
        content={t('general.messages.warning.delete-message')}
        handleClose={cancelDeleteAttempt}
        handleConfirm={handleConfirm}
      />
    </>
  );
}
